import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Info, TrendingUp, Shield, Crown, Award, Star, Users, MessageCircle, Clock, DollarSign } from 'lucide-react';
import { MockBadge } from '../common/MockBadge';
import { DataDisclaimer } from '../common/DataDisclaimer';
import { useExampleData } from '../../data/example/ExampleDataProvider';
import { exampleTrustScoreData, TrustScoreData } from '../../data/example/trustScoreData';

interface TrustScoreSystemProps {
  guruId?: string;
  isEditable?: boolean;
  onScoreUpdate?: (newScore: number) => void;
}

export const TrustScoreSystem: React.FC<TrustScoreSystemProps> = ({ 
  guruId, 
  isEditable = false,
  onScoreUpdate 
}) => {
  const { getTrustScoreData, shouldShowExampleData } = useExampleData();
  const isExampleData = shouldShowExampleData('trust-score');
  const [trustScoreData, setTrustScoreData] = useState<TrustScoreData | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Load data from centralized example data when in demo mode
    if (isExampleData) {
      setTimeout(() => {
        setTrustScoreData(getTrustScoreData());
      }, 500);
    }
  }, [guruId, isExampleData, getTrustScoreData]);

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'Seed': return 'text-gray-600 bg-gray-100 dark:text-gray-300 dark:bg-gray-800';
      case 'Sprout': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20';
      case 'Trusted': return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20';
      case 'Elite': return 'text-purple-600 bg-purple-100 dark:text-purple-400 dark:bg-purple-900/20';
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-300 dark:bg-gray-800';
    }
  };

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'Seed': return '🌱';
      case 'Sprout': return '🌿';
      case 'Trusted': return '🛡️';
      case 'Elite': return '👑';
      default: return '🌱';
    }
  };

  const getProgressPercentage = (current: number, next: number) => {
    const range = next - (next - 100);
    return Math.min(100, Math.max(0, ((current - (next - 100)) / range) * 100));
  };

  const renderScoreBreakdown = () => (
    <div className="space-y-4">
      <h4 className="font-semibold text-gray-900 dark:text-white">Score Breakdown</h4>
      <div className="grid grid-cols-2 gap-4">
        {Object.entries(trustScoreData!.scoreBreakdown).map(([key, value]) => (
          <div key={key} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                {key.replace(/([A-Z])/g, ' $1').trim()}
              </span>
              <span className="text-sm font-bold text-gray-900 dark:text-white">{value}</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${value}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderRecentActivity = () => (
    <div className="space-y-4">
      <h4 className="font-semibold text-gray-900 dark:text-white">Recent Activity</h4>
      <div className="space-y-3">
        {trustScoreData!.recentActivity.map((activity, index) => (
          <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
              activity.type === 'positive' 
                ? 'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400'
                : activity.type === 'negative'
                ? 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400'
                : 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
            }`}>
              {activity.type === 'positive' ? (
                <CheckCircle className="w-4 h-4" />
              ) : activity.type === 'negative' ? (
                <XCircle className="w-4 h-4" />
              ) : (
                <Info className="w-4 h-4" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-900 dark:text-white">{activity.description}</p>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">{activity.date}</span>
                <span className={`text-xs font-medium ${
                  activity.impact > 0 
                    ? 'text-green-600 dark:text-green-400'
                    : activity.impact < 0
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {activity.impact > 0 ? '+' : ''}{activity.impact} points
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (!trustScoreData) {
    return (
      <div className="animate-pulse">
        <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {isExampleData && (
        <DataDisclaimer 
          type="info"
          title="Example Trust Score"
          message="This is example trust score data for demonstration. Real trust scores will be calculated based on your actual performance and client interactions."
          className="mb-4"
        />
      )}
      {/* Main Trust Score Display */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Trust Score</h3>
            {isExampleData && <MockBadge variant="small" />}
          </div>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
          >
            {showDetails ? 'Hide Details' : 'Show Details'}
          </button>
        </div>

        <div className="flex items-center space-x-4 mb-6">
          <div className="flex-shrink-0">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-2xl">
              {trustScoreData.currentScore}
            </div>
          </div>
          
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">{getTierIcon(trustScoreData.tier)}</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTierColor(trustScoreData.tier)}`}>
                {trustScoreData.tier} Tier
              </span>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Progress to next tier</span>
                <span className="text-gray-900 dark:text-white font-medium">
                  {trustScoreData.currentScore} / {trustScoreData.nextTierScore}
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${getProgressPercentage(trustScoreData.currentScore, trustScoreData.nextTierScore)}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <DollarSign className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Platform Fee</span>
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">{trustScoreData.platformFee}%</span>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Shield className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Current Tier</span>
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">{trustScoreData.tier}</span>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <TrendingUp className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Next Tier</span>
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">{trustScoreData.nextTierScore}</span>
          </div>
        </div>

        {showDetails && (
          <div className="space-y-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            {renderScoreBreakdown()}
            {renderRecentActivity()}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 dark:text-white">Current Perks</h4>
                <div className="space-y-2">
                  {trustScoreData.perks.map((perk, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{perk}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 dark:text-white">Requirements for Next Tier</h4>
                <div className="space-y-2">
                  {trustScoreData.requirements.map((requirement, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{requirement}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 
